import type { AppProps } from 'next/app'
import type { DefaultTheme } from 'styled-components'
import type { StatsigInitializeValues } from 'utils/feature-flag/instance'

import { datadogRum } from '@datadog/browser-rum'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
/* eslint-disable-next-line import/order */
import { AppCacheProvider } from '@mui/material-nextjs/v13-pagesRouter'
import { muiTheme } from '@rentspree/component-2023.theme.mui'
import Head from 'next/head'
import Script from 'next/script'
import { ThemeProvider } from 'styled-components'

import { AuthProvider } from 'containers/auth'
import { FeatureFlagProvider } from 'containers/feature-flag'
import { UserProfileProvider } from 'containers/user-profile'
import env from 'env'
import { withRedux } from 'store'
import GlobalStyle from 'styles/index'
import styledTheme from 'styles/theme'

import '@fortawesome/fontawesome-free/css/all.min.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'react-image-gallery/styles/scss/image-gallery.scss'
import 'react-image-gallery/styles/css/image-gallery.css'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import 'containers/public-listing/public-listing.scss'

import packageJson from '../../package.json'

if (
  typeof window !== 'undefined' &&
  env.DATADOG_CLIENT_TOKEN &&
  env.DATADOG_APPLICATION_ID &&
  env.DATADOG_SITE
) {
  datadogRum.init({
    applicationId: env.DATADOG_APPLICATION_ID,
    clientToken: env.DATADOG_CLIENT_TOKEN,
    site: env.DATADOG_SITE,
    service: packageJson.name,
    env: env.ENVIRONMENT,
    version: env.DATADOG_VERSION,
    sessionSampleRate: parseInt(env.DATADOG_SAMPLE_RATE || '0', 10),
    sessionReplaySampleRate: parseInt(env.DATADOG_REPLAY_SAMPLE_RATE || '0', 10),
    traceSampleRate: parseInt(env.DATADOG_TRACE_SAMPLE_RATE || '0', 10),
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [
      { match: env.API_URL || '', propagatorTypes: ['b3', 'b3multi', 'tracecontext'] },
      { match: env.USER_API_URL || '', propagatorTypes: ['b3', 'b3multi', 'tracecontext'] },
    ],
    excludedActivityUrls: [
      /^https:\/\/api\.amplitude\.com/,
      /^https:\/\/analytics\.google\.com/,
      /^https:\/\/.*\.clarity\.ms/,
      /^https:\/\/settings\.luckyorange\.net/,
      /^https:\/\/.*\.talkjs\.com/,
      /^https:\/\/status\.rentspree\.com\/embed\/script\.js/,
      /^https:\/\/.*\.statuspage\.io/,
      /^https:\/\/.*\.hs-scripts\.com/,
      /^https:\/\/.*\.hs-banner\.com/,
      /^https:\/\/.*\.hs-analytics\.net/,
      /^https:\/\/.*\.hubspotfeedback\.com/,
      /^https:\/\/.*\.hubspot\.com/,
      /^https:\/\/.*\.hsappstatic\.net/,
      /^https:\/\/.*\.hsforms\.com/,
      /^https:\/\/.*\.hsforms\.net/,
      /^https:\/\/.*\.hsleadflows\.net/,
    ],
  })
}

const StyledThemeProvider = ThemeProvider as unknown as React.FC<{
  theme: DefaultTheme
  children: React.ReactNode
}>
const theme = createTheme(muiTheme)

const App = (props: AppProps): JSX.Element => {
  /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
  const { Component, pageProps } = props
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { initializeValues } = pageProps

  return (
    <>
      <Script>
        {`;(function(w, d, s, l, i) {
            w[l] = w[l] || []
            w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" })
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != "dataLayer" ? "&l=" + l : ""
            j.async = true
            j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl
            f.parentNode.insertBefore(j, f)
          })(
          window,
          document,
          "script",
          "dataLayer",
          "${env.GOOGLE_TAG_MANAGER_CONTAINER_ID}",)`}
      </Script>
      <AppCacheProvider {...props}>
        <Head>
          <title>RentSpree</title>
        </Head>
        <GlobalStyle />
        <StyledThemeProvider theme={styledTheme}>
          <MuiThemeProvider theme={theme}>
            <AuthProvider>
              <UserProfileProvider>
                <FeatureFlagProvider initializeValues={initializeValues as StatsigInitializeValues}>
                  <Component {...pageProps} />
                </FeatureFlagProvider>
              </UserProfileProvider>
            </AuthProvider>
          </MuiThemeProvider>
        </StyledThemeProvider>
      </AppCacheProvider>
    </>
  )
}

export default withRedux(App)
